import { useEffect, type RefObject } from 'react'

const EDITABLE =
  'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]), textarea, select, [contenteditable=""], [contenteditable=true]'
/** When (ms after a focus or a viewport change) the focused field is scrolled into view again: the keyboard animates. */
const SETTLE_MS = [0, 120, 320, 650]

/**
 * Keeps the app shell exactly as tall as what the member can see while the on-screen keyboard is open.
 *
 * Sets on the shell: `--app-vh` (the visual viewport's height) and `--app-top` (its offset inside the layout viewport).
 * Browsers differ: iOS Safari and Chrome with `interactive-widget=resizes-visual` (or without support: Samsung Internet,
 * Firefox, older Chrome) keep the layout viewport and shrink only the visual one, and iOS also scrolls the layout viewport
 * to reveal the focused field, so a `100dvh` shell would stay full height behind the keyboard or slide out of view.
 * Reading both values from `visualViewport` and translating the shell by `--app-top` makes every browser agree; where the
 * layout viewport itself resizes (Chrome `resizes-content`) the values equal the shell's own height and nothing changes.
 * A pinch-zoom shrinks the visual viewport too (scale above 1): the shell then keeps its last size.
 *
 * Focusing a field also scrolls it into view inside the scroller, again once the keyboard animation has settled.
 */
export function useKeyboardInset(root: RefObject<HTMLElement | null>, scroller: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const vv = window.visualViewport
    const el = root.current
    if (!vv || !el) return
    let timers: number[] = []
    const clearTimers = () => {
      timers.forEach((t) => window.clearTimeout(t))
      timers = []
    }
    const reveal = () => {
      const a = document.activeElement
      if (a instanceof HTMLElement && a.matches(EDITABLE) && scroller.current?.contains(a)) {
        a.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      }
    }
    const settle = () => {
      clearTimers()
      timers = SETTLE_MS.map((ms) => window.setTimeout(reveal, ms))
    }
    const update = () => {
      if (vv.scale > 1.01) return
      el.style.setProperty('--app-vh', `${Math.round(vv.height)}px`)
      el.style.setProperty('--app-top', `${Math.max(0, Math.round(vv.offsetTop))}px`)
      settle()
    }
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && e.target.matches(EDITABLE)) settle()
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    el.addEventListener('focusin', onFocusIn)
    return () => {
      clearTimers()
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      el.removeEventListener('focusin', onFocusIn)
      el.style.removeProperty('--app-vh')
      el.style.removeProperty('--app-top')
    }
  }, [root, scroller])
}
